/**
 * Canonical PeriodPicker value → API query params.
 *
 * The picker resolves presets ("30d") to explicit start/end instants in the
 * UI; every endpoint receives that exact window, so "Last 30 days" can never
 * mean different things on different screens. Backends keep preset-code
 * fallbacks (shared/period.py) for legacy callers, but anything sending
 * these params is window-exact.
 *
 * Why this exists (2026-06-10 METIS review): /overview anchored preset codes
 * at today − 2 days midnight while the Curtailment page used the picker's
 * literal instants — the same nominal 30-day window showed 694 MWh curtailed
 * on one Technical tab and 396 MWh on another.
 */

/**
 * Standard ISO-instant params for routes resolved by shared/period.py
 * (/overview, /technical, /dispatch, /outages, /forecast-accuracy,
 * /clearing-price, /benchmark-series, …).
 *
 * The period code is still sent — the backend uses it only to label the
 * window ("Last 30 days" vs "Custom · 14 days").
 */
export function periodApiParams(period, { withFrequency = true } = {}) {
  const params = {};
  if (period?.code) params.period = period.code;
  if (period?.start) params.start = period.start;
  if (period?.end) params.end = period.end;
  if (withFrequency && period?.frequency) params.frequency = period.frequency;
  return params;
}

/**
 * INT64 YYYYMMDDHHMM window bounds for the modules/performance routes
 * (/curtailment, /curtailment/constraints), which take digit bounds read as
 * NEM wall clock.
 *
 * Digits come from the ISO instant's UTC fields — SETTLEMENTDATE is stored
 * NEM-clock-as-UTC, so this resolves the picker instant to the exact same
 * wall clock every ISO-based endpoint compares against. Using LOCAL date
 * fields here (the pre-2026-06-10 bug) skewed the window by the viewer's
 * UTC offset, so the Curtailment tab and Technical Performance disagreed.
 */
export function periodNemInts(period) {
  const toInt = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const p = (n) => String(n).padStart(2, '0');
    return parseInt(
      `${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}`
      + `${p(d.getUTCHours())}${p(d.getUTCMinutes())}`,
      10,
    );
  };
  return { start: toInt(period?.start), end: toInt(period?.end) };
}
